import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../../utils/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Building2, CheckCircle2, XCircle, Globe, Star, Search, Loader2, Mail, KeyRound,
  ClipboardList, Phone, MessageSquare, AlertCircle, RefreshCw, ArrowRightLeft, History,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';

import { formatDate } from '../../utils/timeFormat';
/**
 * Iter-220b — Client Approvals queue (gap-fix).
 *
 * Mobile-first card layout (collapses table on <md). 5 status tabs with
 * counts. Search across company / contact / email. Detail drawer
 * exposes intake answers, audit timeline, internal notes, resend-email
 * + resend-magic-link + toggle-priority + convert-to-talent actions.
 * Mobile sticky approve/reject bar when a card is open.
 *
 * Backed by:
 *   GET    /api/admin/client-approvals?status=&q=
 *   POST   /api/admin/client-approvals/{id}/approve|reject|convert-to-talent
 *   POST   /api/admin/client-approvals/{id}/resend-onboarding-email
 *   POST   /api/admin/client-approvals/{id}/resend-magic-link
 *   POST   /api/admin/client-approvals/{id}/toggle-priority
 *   PATCH  /api/admin/client-approvals/{id}/internal-notes
 *   GET    /api/admin/client-approvals/{id}/audit
 */

const TABS = [
  { key: 'pending',         label: 'Pending',         icon: ClipboardList },
  { key: 'missing_intake',  label: 'Missing intake',  icon: AlertCircle },
  { key: 'approved',        label: 'Approved',        icon: CheckCircle2 },
  { key: 'rejected',        label: 'Rejected',        icon: XCircle },
  { key: 'converted',       label: 'Converted',       icon: ArrowRightLeft },
];

const statusBadge = (s) => {
  switch (s) {
    case 'pending_approval': return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Pending</Badge>;
    case 'approved':         return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Approved</Badge>;
    case 'rejected':         return <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100">Rejected</Badge>;
    default:                 return <Badge variant="outline">{s || '—'}</Badge>;
  }
};

const ClientApprovals = () => {
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window === 'undefined') return 'pending';
    const params = new URLSearchParams(window.location.search);
    return params.get('status') || 'pending';
  });
  const [q, setQ] = useState('');
  const [data, setData] = useState({ clients: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [audit, setAudit] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [busyAction, setBusyAction] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectNotes, setRejectNotes] = useState('');
  const [notesDraft, setNotesDraft] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: activeTab });
      if (q.trim()) params.append('q', q.trim());
      const r = await api.get(`/admin/client-approvals?${params}`);
      setData(r.data || { clients: [], counts: {} });
    } catch (e) {
      toast.error('Failed to load approvals');
    } finally {
      setLoading(false);
    }
  }, [activeTab, q]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (c) => {
    setDetail(c);
    setNotesDraft(c.admin_notes || '');
    setAuditLoading(true);
    try {
      const r = await api.get(`/admin/client-approvals/${c.user_id}/audit`);
      setAudit(r.data?.events || r.data?.audit || []);
    } catch (e) {
      setAudit([]);
    } finally {
      setAuditLoading(false);
    }
  };

  const refresh = async () => { await load(); };

  const approve = async (c) => {
    setBusyAction('approve');
    try {
      await api.post(`/admin/client-approvals/${c.user_id}/approve`, {});
      toast.success(`${c.company_name || c.email} approved`);
      setDetail(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approve failed');
    } finally { setBusyAction(null); }
  };

  const reject = async () => {
    if (!detail || !rejectReason) { toast.error('Reason required'); return; }
    setBusyAction('reject');
    try {
      await api.post(`/admin/client-approvals/${detail.user_id}/reject`, {
        reason: rejectReason, notes: rejectNotes || null,
      });
      toast.success(`${detail.company_name || detail.email} rejected`);
      setRejectOpen(false);
      setRejectReason('');
      setRejectNotes('');
      setDetail(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reject failed');
    } finally { setBusyAction(null); }
  };

  const resendOnboarding = async (c) => {
    setBusyAction('resend_onboarding');
    try {
      const r = await api.post(`/admin/client-approvals/${c.user_id}/resend-onboarding-email`, {});
      r.data?.sent ? toast.success('Onboarding email resent') : toast.error(r.data?.error || 'Send failed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resend failed');
    } finally { setBusyAction(null); }
  };

  const resendMagic = async (c) => {
    setBusyAction('resend_magic');
    try {
      const r = await api.post(`/admin/client-approvals/${c.user_id}/resend-magic-link`, {});
      r.data?.sent ? toast.success('Magic link resent') : toast.error(r.data?.error || 'Send failed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resend failed');
    } finally { setBusyAction(null); }
  };

  const togglePriority = async (c) => {
    setBusyAction('priority');
    try {
      await api.post(`/admin/client-approvals/${c.user_id}/toggle-priority`, {
        is_high_priority: !c.is_high_priority,
      });
      toast.success(c.is_high_priority ? 'Removed high-priority flag' : 'Flagged as high priority');
      setDetail(d => d ? { ...d, is_high_priority: !c.is_high_priority } : null);
      refresh();
    } catch (e) {
      toast.error('Failed to toggle priority');
    } finally { setBusyAction(null); }
  };

  const saveNotes = async () => {
    if (!detail) return;
    setBusyAction('notes');
    try {
      await api.patch(`/admin/client-approvals/${detail.user_id}/internal-notes`, { notes: notesDraft });
      toast.success('Internal notes saved');
      setDetail(d => d ? { ...d, admin_notes: notesDraft } : null);
      refresh();
    } catch (e) {
      toast.error('Save failed');
    } finally { setBusyAction(null); }
  };

  const convert = async (c) => {
    if (!window.confirm(`Convert "${c.company_name || c.email}" to a talent account?`)) return;
    setBusyAction('convert');
    try {
      await api.post(`/admin/users/${c.user_id}/convert-to-talent`, {});
      toast.success('Converted to talent');
      setDetail(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Convert failed');
    } finally { setBusyAction(null); }
  };

  const tabs = useMemo(() => TABS.map(t => ({
    ...t,
    count: data.counts?.[t.key] ?? 0,
  })), [data.counts]);

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-5 sm:px-8 overflow-x-hidden" data-testid="client-approvals-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-2 mb-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
              <Building2 className="w-6 h-6 text-cyan-600" /> Client Approvals
            </h1>
            <p className="text-sm text-gray-500">
              Review, approve, and onboard new client accounts.
              {data.counts?.high_priority > 0 && (
                <span className="ml-2 text-amber-700 font-medium">
                  · {data.counts.high_priority} flagged high-priority
                </span>
              )}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={refresh} disabled={loading} data-testid="client-approvals-refresh">
            {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
            Refresh
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 mb-3" role="tablist">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border ${activeTab === t.key ? 'bg-cyan-600 text-white border-cyan-600' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}
              data-testid={`client-approvals-tab-${t.key}`}
              role="tab"
              aria-selected={activeTab === t.key}
            >
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
              {t.count > 0 && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${activeTab === t.key ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-700'}`}>
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search company, contact, or email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
            data-testid="client-approvals-search"
          />
        </div>

        {/* Cards (mobile-first) */}
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
        ) : data.clients.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-gray-500" data-testid="client-approvals-empty">
            No clients in this view.
          </CardContent></Card>
        ) : (
          <ul className="space-y-2" data-testid="client-approvals-list">
            {data.clients.map(c => (
              <li key={c.user_id} data-testid={`client-approvals-row-${c.user_id}`}>
                <Card className="hover:shadow-sm transition-shadow">
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          {c.is_high_priority && (
                            <Star className="w-4 h-4 text-amber-500 fill-amber-400 shrink-0" />
                          )}
                          <h3 className="font-semibold text-gray-900 truncate" data-testid="client-company-name">
                            {c.company_name || '(no company name)'}
                          </h3>
                          {/* Iter-225q — Account-type badge so admins immediately
                              distinguish Full-Service Client vs Agency Workspace in the
                              same approval queue. */}
                          {c.account_type === 'agency' ? (
                            <Badge
                              data-testid={`account-type-agency-${c.user_id}`}
                              className="text-[10px] uppercase tracking-wider bg-purple-100 text-purple-800 hover:bg-purple-100"
                            >
                              Agency Workspace
                            </Badge>
                          ) : (
                            <Badge
                              data-testid={`account-type-client-${c.user_id}`}
                              className="text-[10px] uppercase tracking-wider bg-cyan-100 text-cyan-800 hover:bg-cyan-100"
                            >
                              Full-Service Client
                            </Badge>
                          )}
                          {statusBadge(c.approval_status)}
                          {c.is_converted && (
                            <Badge variant="outline" className="text-[10px]">converted</Badge>
                          )}
                          {c.intake_missing && (
                            <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 text-[10px]">missing intake</Badge>
                          )}
                        </div>
                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600">
                          {c.contact_name && <span className="truncate">{c.contact_name}</span>}
                          {c.email && <span className="flex items-center gap-1 min-w-0"><Mail className="w-3 h-3" /><span className="truncate">{c.email}</span></span>}
                          {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>}
                          {c.intake?.industry && <span>· {c.intake.industry}</span>}
                          {c.intake?.budget_range && <span>· {c.intake.budget_range}</span>}
                          {c.intake?.company_size && <span>· {c.intake.company_size}</span>}
                          {c.website && (
                            <span className="flex items-center gap-1"><Globe className="w-3 h-3" />
                              <a href={c.website} target="_blank" rel="noreferrer" className="text-cyan-700 truncate">{c.website}</a>
                            </span>
                          )}
                          {c.created_at && <span>· Submitted {formatDate(c.created_at)}</span>}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 shrink-0">
                        <Button size="sm" variant="outline" onClick={() => openDetail(c)} data-testid={`client-approvals-view-${c.user_id}`}>
                          <FileText className="w-3 h-3 mr-1" /> View
                        </Button>
                        {c.approval_status === 'pending_approval' && (
                          <>
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => approve(c)} data-testid={`client-approvals-approve-${c.user_id}`}>
                              <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
                            </Button>
                            <Button size="sm" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => { setDetail(c); setRejectOpen(true); }} data-testid={`client-approvals-reject-${c.user_id}`}>
                              <XCircle className="w-3 h-3 mr-1" /> Reject
                            </Button>
                            {/* Iter-227d — Convert to Talent inline on pending rows so admins
                                can fix mis-categorized signups without opening the drawer. */}
                            {!c.is_converted && (
                              <Button size="sm" variant="outline" className="border-purple-300 text-purple-700 hover:bg-purple-50" onClick={() => convert(c)} data-testid={`client-approvals-convert-${c.user_id}`}>
                                <ArrowRightLeft className="w-3 h-3 mr-1" /> Convert to Talent
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Detail drawer */}
      <Dialog open={!!detail && !rejectOpen} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto pb-24 sm:pb-6" data-testid="client-approvals-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  {detail.is_high_priority && <Star className="w-4 h-4 text-amber-500 fill-amber-400" />}
                  {detail.company_name || detail.email}
                  {statusBadge(detail.approval_status)}
                </DialogTitle>
                <DialogDescription>
                  {detail.contact_name && <>{detail.contact_name} · </>}
                  {detail.email}
                  {detail.phone && <> · {detail.phone}</>}
                </DialogDescription>
              </DialogHeader>

              {/* Quick action chips */}
              <div className="flex flex-wrap gap-1.5" data-testid="client-approvals-actions">
                <Button size="sm" variant="outline" onClick={() => togglePriority(detail)} disabled={busyAction === 'priority'} data-testid="client-approvals-toggle-priority">
                  <Star className={`w-3 h-3 mr-1 ${detail.is_high_priority ? 'text-amber-500 fill-amber-400' : ''}`} />
                  {detail.is_high_priority ? 'Unflag priority' : 'Flag high priority'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => resendOnboarding(detail)} disabled={busyAction === 'resend_onboarding'} data-testid="client-approvals-resend-onboarding">
                  <Mail className="w-3 h-3 mr-1" /> Resend onboarding
                </Button>
                <Button size="sm" variant="outline" onClick={() => resendMagic(detail)} disabled={busyAction === 'resend_magic'} data-testid="client-approvals-resend-magic">
                  <KeyRound className="w-3 h-3 mr-1" /> Resend magic link
                </Button>
                {/* Iter-227d — Convert to Talent is now available for ALL
                    non-converted client records, including pending/rejected
                    ones, because admins frequently realize a signup was a
                    mis-categorized talent BEFORE approving them. Backend
                    accepts any role=client; UI gate only blocks
                    already-converted accounts. */}
                {!detail.is_converted && (
                  <Button size="sm" variant="outline" onClick={() => convert(detail)} disabled={busyAction === 'convert'} data-testid="client-approvals-convert">
                    <ArrowRightLeft className="w-3 h-3 mr-1" /> Convert to talent
                  </Button>
                )}
              </div>

              {/* Intake answers */}
              <Card className="bg-gray-50 border-gray-200">
                <CardContent className="p-3 space-y-1">
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Intake answers</p>
                  {detail.intake_missing ? (
                    <p className="text-sm text-rose-700">No intake form submitted yet.</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
                      {[
                        ['Industry', detail.intake?.industry],
                        ['Company size', detail.intake?.company_size],
                        ['Budget range', detail.intake?.budget_range],
                        ['Timeline', detail.intake?.timeline],
                        ['Event types', detail.intake?.event_types],
                        ['Website', detail.website],
                      ].filter(([, v]) => v).map(([k, v]) => (
                        <div key={k}><span className="text-gray-500">{k}:</span> <span className="text-gray-900">{v}</span></div>
                      ))}
                      {detail.intake?.notes && (
                        <div className="sm:col-span-2">
                          <span className="text-gray-500">Notes:</span>
                          <p className="text-gray-900 whitespace-pre-wrap mt-0.5">{detail.intake.notes}</p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Internal notes */}
              <div>
                <Label className="text-xs uppercase tracking-wide text-gray-500">Internal admin notes</Label>
                <Textarea rows={3} value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)} placeholder="Visible to admins only" data-testid="client-approvals-notes-textarea" />
                <Button size="sm" variant="outline" className="mt-1" onClick={saveNotes} disabled={busyAction === 'notes' || notesDraft === (detail.admin_notes || '')} data-testid="client-approvals-notes-save">
                  Save notes
                </Button>
              </div>

              {/* Audit timeline */}
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-1 flex items-center gap-1"><History className="w-3 h-3" /> Audit timeline</p>
                {auditLoading ? (
                  <p className="text-xs text-gray-500"><Loader2 className="w-3 h-3 animate-spin inline" /> Loading…</p>
                ) : audit.length === 0 ? (
                  <p className="text-xs text-gray-500">No history yet.</p>
                ) : (
                  <ul className="text-xs text-gray-700 divide-y divide-gray-100 max-h-40 overflow-y-auto" data-testid="client-approvals-audit-list">
                    {audit.map((a, idx) => (
                      <li key={a.id || idx} className="py-1.5">
                        <span className="font-medium">{a.action}</span>
                        {a.admin_email && <> by <span className="text-gray-600">{a.admin_email}</span></>}
                        {a.reason && <> · {a.reason}</>}
                        {a.created_at && <span className="text-gray-400"> · {new Date(a.created_at).toLocaleString()}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <DialogFooter className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 z-10 flex gap-2" data-testid="client-approvals-sticky-actions">
                {detail.approval_status === 'pending_approval' && (
                  <>
                    <Button onClick={() => { setRejectOpen(true); }} variant="outline" className="flex-1 border-rose-300 text-rose-700 hover:bg-rose-50">
                      <XCircle className="w-4 h-4 mr-1" /> Reject
                    </Button>
                    <Button onClick={() => approve(detail)} disabled={busyAction === 'approve'} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white">
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                    </Button>
                  </>
                )}
              </DialogFooter>
              <div className="hidden sm:flex justify-end gap-2 pt-2 border-t border-gray-100">
                <Button variant="outline" onClick={() => setDetail(null)}>Close</Button>
                {detail.approval_status === 'pending_approval' && (
                  <>
                    <Button onClick={() => { setRejectOpen(true); }} variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50">
                      <XCircle className="w-3 h-3 mr-1" /> Reject
                    </Button>
                    <Button onClick={() => approve(detail)} disabled={busyAction === 'approve'} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
                    </Button>
                  </>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="max-w-md" data-testid="client-approvals-reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject client</DialogTitle>
            <DialogDescription>
              {detail && <>Rejecting <strong>{detail.company_name || detail.email}</strong>. Reason is sent to the client; admin notes are internal.</>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-gray-500">Reason (sent to client)</Label>
            <Textarea rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="e.g. Unable to verify company; please reply with EIN documentation." data-testid="client-approvals-reject-reason" />
            <Label className="text-xs uppercase tracking-wide text-gray-500">Internal admin notes (optional)</Label>
            <Textarea rows={2} value={rejectNotes} onChange={(e) => setRejectNotes(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button onClick={reject} disabled={!rejectReason || busyAction === 'reject'} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="client-approvals-reject-submit">
              {busyAction === 'reject' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
              Reject & notify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientApprovals;
